import type { User } from '@braze/web-sdk'
import type { ProductVariant } from '@getstep/proto/common'
import { notNullish } from '@getstep/sdk/dist/util/Assert'
import {
    initAppsFlyerPBA,
    initOneLinkSmartScript,
} from '@lib/analytics/appsflyer'
import { isDefaultUtmValue, PII_KEYS } from '@lib/analytics/constants'
import { AnalyticsEvent } from '@lib/analytics/events'
import * as mixpanel from '@lib/analytics/mixpanel'
import {
    getSegmentAnalytics,
    initSegment,
    segmentIdentify,
    segmentTrack,
} from '@lib/analytics/segment'
import { generateEventId } from '@lib/analytics/utils'
import { hasCookieConsent } from '@lib/hooks/useCookieConsent'
import { localStorageWithTTL } from '@lib/store/persistence/storage'
import * as Sentry from '@sentry/nextjs'
import { isPrd } from '@shared/util/env'
import { detectSocialMediaWebView } from '@shared/util/webview-detection'

export interface Braze {
    logCustomEvent: (eventName: string, eventProperties?: object) => boolean
    changeUser: (userId: string, signature?: string) => void
    getUser: () => User | undefined
}

// Mapping of events that have different names in Segment
const SEGMENT_EVENT_MAP: Record<string, AnalyticsEvent> = {
    [AnalyticsEvent.SIGNUP]: AnalyticsEvent.SIGNUP_SEGMENT,
}

// Whitelist of events that should be sent to Segment
const SEGMENT_ALLOWED_EVENTS = new Set<string>([
    AnalyticsEvent.SIGNUP_SEGMENT,
    AnalyticsEvent.KYC_ATTEMPT,
    AnalyticsEvent.TRIAL_STARTED,
    AnalyticsEvent.LINKED_ACCOUNT_ADDED,
    AnalyticsEvent.EARLYPAY_APPLICATION_SUBMITTED,
    // Google Ads conversion events (also enables Facebook CAPI via Segment)
    AnalyticsEvent.EARLYPAY_FLOW_STARTED,
    AnalyticsEvent.EARLYPAY_USER_VERIFIED,
    AnalyticsEvent.KYC_SUCCESS,
    AnalyticsEvent.LINKED_DEBIT_CARD_ADDED,
    AnalyticsEvent.EARLYPAY_UNDERWRITING_APPROVED,
    // Test event for /test/analytics page - easily filterable by [TEST] prefix
    '[TEST] Segment Whitelist',
])

export interface UserAttributes {
    email?: string
    phone?: string
    firstName?: string
    lastName?: string
    dateOfBirth?: string // For Braze and internal use
    birthday?: string // Segment reserved trait (ISO-8601 date format: YYYY-MM-DD)
    productVariant?: ProductVariant
    platform?: string
    userAgent?: string
    ip?: string
    // Meta Pixel traits for Facebook CAPI
    fbc?: string | null
    fbp?: string | null
    fbclid?: string | null
    // UTM parameters
    utm_source?: string
    utm_campaign?: string
    utm_ad?: string
    utm_medium?: string
    utm_id?: string
    utm_content?: string
    utm_term?: string
    // Google Ads parameters
    gclid?: string
    adset?: string
    network?: string
    matchtype?: string
    device?: string
    // Google iOS 14.5+ conversion tracking
    wbraid?: string
    gbraid?: string
    // TikTok parameters
    ttclid?: string
    // Snapchat parameters
    ScCid?: string
    // Impact Radius parameters
    impactClickId?: string
}

/**
 * Segment track context for Facebook CAPI and attribution
 */
interface SegmentTrackContext {
    ip?: string
    userAgent?: string
    traits?: { email?: string; phone?: string }
    referrer?: { id: string; type: string }
    page?: { url?: string }
}

function debugLog(...args: unknown[]) {
    if (!isPrd()) {
        console.info('[AnalyticsService]', ...args)
    }
}

function splitDateWithRegex(dateString: string) {
    // Try ISO format first (YYYY-MM-DD)
    const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (isoMatch) {
        const [, year, month, day] = isoMatch
        return {
            day: parseInt(day, 10),
            month: parseInt(month, 10),
            year: parseInt(year, 10),
        }
    }

    // Try DD/MM/YYYY format (with optional spaces around slashes)
    const dmyMatch = dateString.match(/^(\d{2})\s?\/\s?(\d{2})\s?\/\s?(\d{4})$/)
    if (dmyMatch) {
        const [, day, month, year] = dmyMatch
        return {
            day: parseInt(day, 10),
            month: parseInt(month, 10),
            year: parseInt(year, 10),
        }
    }

    throw new Error(
        `Invalid date format. Expected "YYYY-MM-DD", "DD/MM/YYYY", or "DD / MM / YYYY" but received "${dateString}"`,
    )
}

class AnalyticsService {
    initialized = false
    braze: Braze | undefined
    private attributionData: Record<string, any> = {}
    private pendingIdentity?: { userId: string; attributes: UserAttributes }
    private pendingEvents: Array<{
        type: 'track' | 'pageView'
        name: string
        properties: Record<string, any>
        eventId: string
    }> = []
    private currentUserTraits: { email?: string; phone?: string } = {}
    private browserContext: {
        userAgent?: string
        ip?: string
    } = {}
    private ipFetchPromise?: Promise<string>
    private ipFetchFailed = false // Track if IP fetch has failed to avoid repeated attempts
    private hasLoggedBlockedProviders = false // Rate limit: only log blocked providers once per session

    // Memory leak fix: Limit pending events queue size
    private readonly MAX_PENDING_EVENTS = 1000
    // Memory leak fix: Limit attribution data object size
    private readonly MAX_ATTRIBUTION_KEYS = 60

    // SessionStorage keys
    private readonly PENDING_EVENTS_KEY = 'step_analytics_pending_events'
    private readonly PENDING_IDENTITY_KEY = 'step_analytics_pending_identity'
    private readonly ATTRIBUTION_DATA_KEY = 'step_analytics_attribution_data'
    // localStorage key for original/first-touch attribution (persists 30 days)
    private readonly ORIGINAL_ATTRIBUTION_DATA_KEY =
        'step_analytics_original_attribution'
    // TTL for original attribution data (30 days in minutes)
    private readonly ORIGINAL_ATTRIBUTION_TTL_MINUTES = 30 * 24 * 60
    // Original attribution data (first-touch, persisted in localStorage)
    private originalAttributionData: Record<string, any> = {}
    // Impact Radius click ID for Segment referrer context
    private impactClickId: string | null = null
    private readonly IMPACT_CLICK_ID_KEY = 'step_analytics_impact_click_id'

    async initialize(searchParams?: URLSearchParams) {
        if (this.initialized || typeof window === 'undefined') {
            return
        }
        try {
            // Load any pending data from previous session
            this.loadPendingDataFromStorage()

            // Dynamically import Braze SDK only on client side
            const brazeModule = await import('@braze/web-sdk')
            this.braze = brazeModule

            // Braze SDK requires a full URL for baseUrl
            // The SDK appends paths like /data/ to this base URL
            // Our proxy at /a/brz prepends /api/v3/ to all paths
            const brazeBaseUrl =
                typeof window !== 'undefined'
                    ? `${window.location.origin}/a/brz`
                    : (process.env.NEXT_PUBLIC_BRAZE_BASE_URL ?? '')

            brazeModule.initialize(
                process.env.NEXT_PUBLIC_BRAZE_API_KEY ?? '',
                {
                    baseUrl: brazeBaseUrl, // proxy through Next.js rewrite to bypass ad blockers
                    enableLogging: false,
                    sessionTimeoutInSeconds: 30,
                    minimumIntervalBetweenTriggerActionsInSeconds: 5,
                    openCardsInNewTab: true,
                    allowUserSuppliedJavascript: false,
                    localization: 'en',
                },
            )

            // Open session
            brazeModule.openSession()

            mixpanel.init(searchParams)

            // Initialize Segment with proxy configuration
            await initSegment()

            // Initialize AppsFlyer via proxy (replaces GTM tags)
            initAppsFlyerPBA()
            initOneLinkSmartScript()

            this.initialized = true

            // Detect and log blocked providers once per session
            // Uses polling with early exit instead of fixed timeout
            this.startBlockedProviderDetection()

            // Start IP fetch in background (don't await - allows events to proceed without blocking)
            this.getClientIp()
        } catch (error) {
            if (!isPrd()) {
                console.error('Failed to initialize Analytics:', error)
            }
            Sentry.captureException(error, {
                tags: {
                    errorType: 'initialization_failed',
                    analytics: 'analytics_service',
                },
            })
        }
    }

    // Page tracking
    async trackPageView(
        properties: Record<
            string,
            string | boolean | number | Record<string, string>
        > = {},
    ) {
        if (!this.initialized) {
            await this.initialize()
        }

        const eventName = AnalyticsEvent.PAGE_VIEWED
        const eventId = generateEventId()
        const context = await this.getBrowserContext()

        // Merge properties with attribution data (current + original), browser context, and event_id
        const enrichedProperties = {
            ...this.attributionData,
            ...this.getOriginalAttributionData(),
            ...properties,
            event_id: eventId,
            userAgent: context.userAgent,
            ip: context.ip,
        }

        // Check cookie consent - if not granted, queue for later
        if (!hasCookieConsent()) {
            // Memory leak fix: Enforce max pending events
            if (this.pendingEvents.length >= this.MAX_PENDING_EVENTS) {
                // Remove oldest event to make room (FIFO)
                this.pendingEvents.shift()
                if (!isPrd()) {
                    console.warn(
                        `Analytics pending events queue is full (${this.MAX_PENDING_EVENTS}). Dropping oldest event.`,
                    )
                }
            }
            this.pendingEvents.push({
                type: 'pageView',
                name: eventName,
                properties: enrichedProperties,
                eventId,
            })
            this.savePendingEventsToStorage()
            return
        }

        // Track blocked providers for page views
        const blockedProviders: string[] = []

        mixpanel.track(eventName, {
            ...enrichedProperties,
            hasCookieConsent: hasCookieConsent(),
        })

        if (this.braze) {
            this.braze.logCustomEvent(eventName, {
                ...enrichedProperties,
                hasCookieConsent: hasCookieConsent(),
            })
        } else {
            blockedProviders.push('braze')
        }

        if (window.AF) {
            window.AF('pba', 'event', {
                ...enrichedProperties,
                hasCookieConsent: hasCookieConsent(),
                eventType: 'EVENT',
                eventName,
            })
        } else {
            blockedProviders.push('appsflyer')
        }

        // Log individual breadcrumbs per blocked provider for granular filtering
        for (const provider of blockedProviders) {
            Sentry.addBreadcrumb({
                category: 'analytics',
                message: `Page view blocked: ${eventName}`,
                level: 'warning',
                data: {
                    provider,
                    path: properties.path,
                    analytics: provider,
                },
            })
        }
    }

    // Custom event tracking
    async trackEvent(
        eventName: AnalyticsEvent | string,
        properties: Record<
            string,
            | string
            | boolean
            | number
            | Record<string, string>
            | undefined
            | null
        > = {},
    ) {
        if (!this.initialized) {
            await this.initialize()
        }

        // Use passed event_id if available, otherwise generate a new one
        const eventId =
            (properties.event_id as string | undefined) || generateEventId()
        const context = await this.getBrowserContext()

        // Merge properties with attribution data (current + original), browser context, and event_id
        const enrichedProperties = {
            ...this.attributionData,
            ...this.getOriginalAttributionData(),
            ...properties,
            event_id: eventId,
            userAgent: context.userAgent,
            ip: context.ip,
        }

        // Log to Sentry when SIGNUP fires without attribution
        // This catches ALL cases of missing utm_source, not just SessionStore race condition
        if (
            (
                [
                    AnalyticsEvent.SIGNUP,
                    AnalyticsEvent.SIGNUP_SEGMENT,
                ] as string[]
            ).includes(eventName) &&
            !this.attributionData.utm_source
        ) {
            Sentry.captureMessage('SIGNUP event fired without utm_source', {
                level: 'warning',
                tags: {
                    analytics: 'attribution',
                    errorType: 'missing_utm_source',
                    socialMediaPlatform: detectSocialMediaWebView(),
                    isWebView: /wv|WebView/i.test(navigator.userAgent)
                        ? 'true'
                        : 'false',
                    deviceType: /iPhone|iPad|iPod/i.test(navigator.userAgent)
                        ? 'ios'
                        : /Android/i.test(navigator.userAgent)
                          ? 'android'
                          : 'desktop',
                },
                extra: {
                    eventName,
                    url: window.location.href,
                    referrer: document.referrer || 'none',
                    userAgent: navigator.userAgent,
                    urlParams: Object.fromEntries(
                        new URLSearchParams(window.location.search).entries(),
                    ),
                    attributionDataKeys: Object.keys(this.attributionData),
                    sessionStorageAttribution:
                        sessionStorage.getItem(
                            'step_analytics_attribution_data',
                        ) || 'not_set',
                    performanceNow: Math.round(performance.now()),
                },
            })
        }

        // Map event name to Segment-specific name if needed
        const segmentEventName = SEGMENT_EVENT_MAP[eventName] ?? eventName

        // Check cookie consent - if not granted, queue for later
        if (!hasCookieConsent()) {
            // Memory leak fix: Enforce max pending events
            if (this.pendingEvents.length >= this.MAX_PENDING_EVENTS) {
                // Remove oldest event to make room (FIFO)
                this.pendingEvents.shift()
                if (!isPrd()) {
                    console.warn(
                        `Analytics pending events queue is full (${this.MAX_PENDING_EVENTS}). Dropping oldest event.`,
                    )
                }
            }
            this.pendingEvents.push({
                type: 'track',
                name: eventName,
                properties: enrichedProperties,
                eventId,
            })
            this.savePendingEventsToStorage()
            return
        }

        // Track blocked providers for events
        const blockedProviders: string[] = []

        mixpanel.track(eventName, {
            ...enrichedProperties,
            hasCookieConsent: hasCookieConsent(),
        })

        if (this.braze) {
            this.braze.logCustomEvent(eventName, {
                ...enrichedProperties,
                hasCookieConsent: hasCookieConsent(),
            })
        } else {
            blockedProviders.push('braze')
        }

        // Only send whitelisted events to Segment
        if (SEGMENT_ALLOWED_EVENTS.has(segmentEventName)) {
            debugLog('Segment whitelist check passed for:', segmentEventName)
            if (getSegmentAnalytics()) {
                // Add conversion parameters for ad platforms (Google Ads, Facebook)
                const segmentProperties = this.buildSegmentConversionProperties(
                    {
                        ...enrichedProperties,
                        hasCookieConsent: hasCookieConsent(),
                    },
                    segmentEventName,
                    true,
                )

                debugLog('Segment event:', segmentEventName, segmentProperties)
                segmentTrack(
                    segmentEventName,
                    segmentProperties,
                    this.getSegmentContext(),
                )
            } else {
                debugLog('Segment blocked (window.analytics undefined)')
                blockedProviders.push('segment')
            }
        } else {
            debugLog(
                'Segment whitelist check failed for:',
                segmentEventName,
                'Allowed events:',
                Array.from(SEGMENT_ALLOWED_EVENTS),
            )
        }

        if (window.AF) {
            window.AF('pba', 'event', {
                ...enrichedProperties,
                hasCookieConsent: hasCookieConsent(),
                eventType: 'EVENT',
                eventName,
            })
        } else {
            blockedProviders.push('appsflyer')
        }

        // Log individual breadcrumbs per blocked provider for granular filtering
        for (const provider of blockedProviders) {
            Sentry.addBreadcrumb({
                category: 'analytics',
                message: `Event blocked: ${eventName}`,
                level: 'warning',
                data: {
                    provider,
                    eventName,
                    analytics: provider,
                },
            })
        }

        debugLog('Tracked event:', eventName, enrichedProperties)
    }

    // User identification
    async identifyUser(userId: string, userAttributes: UserAttributes = {}) {
        if (!this.initialized) {
            await this.initialize()
        }

        const context = await this.getBrowserContext()

        // Merge attribution data with user attributes and browser context
        const allAttributes = {
            ...this.attributionData,
            ...userAttributes,
            userAgent: context.userAgent,
            ip: context.ip,
        }

        // Check cookie consent - if not granted, queue for later
        if (!hasCookieConsent()) {
            this.pendingIdentity = { userId, attributes: allAttributes }
            this.savePendingIdentityToStorage()
            return
        }

        // Store previous anonymous ID for reference if needed
        const previousAnonymousId = sessionStorage.getItem('step_session_id')
        if (previousAnonymousId) {
            // Add anonymous ID to attribution data for cross-reference
            this.attributionData.previous_anonymous_id = `anonymous_${previousAnonymousId}`
        }

        // Track blocked providers for identity calls
        const blockedProviders: string[] = []

        if (this.braze) {
            this.braze.changeUser(userId)
        } else {
            blockedProviders.push('braze')
        }

        mixpanel.identifyUser(userId)

        if (window.AF) {
            window.AF('pba', 'setCustomerUserId', userId)
        } else {
            blockedProviders.push('appsflyer')
        }

        // Filter PII from Segment identify - only send email/phone for Facebook CAPI matching
        if (getSegmentAnalytics()) {
            const segmentTraits = this.filterPiiForSegment(allAttributes)
            // Add impactClickId to traits for user profile storage
            if (this.impactClickId) {
                segmentTraits.impactClickId = this.impactClickId
            }
            segmentIdentify(userId, segmentTraits)
        } else {
            blockedProviders.push('segment')
        }

        // Log individual breadcrumbs per blocked provider for granular filtering
        for (const provider of blockedProviders) {
            Sentry.addBreadcrumb({
                category: 'analytics',
                message: 'Identity blocked: identifyUser',
                level: 'warning',
                data: {
                    provider,
                    userId,
                    analytics: provider,
                },
            })
        }

        if (Object.keys(allAttributes).length > 0) {
            await this.setUserAttributes(allAttributes)
        }
    }

    // Set user attributes
    async setUserAttributes(attributes: UserAttributes) {
        if (!this.initialized) {
            await this.initialize()
        }

        // Store email and phone for context.traits in track events
        if (attributes.email) {
            this.currentUserTraits.email = attributes.email
        }
        if (attributes.phone) {
            this.currentUserTraits.phone = attributes.phone
        }

        const user = this.braze?.getUser()
        if (!this.braze) {
            Sentry.addBreadcrumb({
                category: 'analytics',
                message: 'Braze blocked for setUserAttributes',
                level: 'warning',
                data: {
                    provider: 'braze',
                    method: 'setUserAttributes',
                    analytics: 'braze',
                },
            })
        } else if (notNullish(user)) {
            // Standard attributes
            if (attributes.email) user.setEmail(attributes.email)
            if (attributes.firstName) user.setFirstName(attributes.firstName)
            if (attributes.lastName) user.setLastName(attributes.lastName)
            if (attributes.phone) user.setPhoneNumber(attributes.phone)
            try {
                if (attributes.dateOfBirth) {
                    const { day, month, year } = splitDateWithRegex(
                        attributes.dateOfBirth,
                    )
                    user.setDateOfBirth(year, month, day)
                }
            } catch (error) {
                if (!isPrd()) {
                    console.warn(error)
                }
            }

            // Custom attributes
            Object.keys(attributes).forEach((key) => {
                if (
                    ![
                        'email',
                        'firstName',
                        'lastName',
                        'phone',
                        'dateOfBirth',
                    ].includes(key)
                ) {
                    user.setCustomUserAttribute(key, (attributes as any)[key])
                }
            })
        }
        // Filter PII from Segment identify - only send email/phone for Facebook CAPI matching
        if (getSegmentAnalytics()) {
            const segmentTraits = this.filterPiiForSegment(attributes)
            // Add impactClickId to traits for user profile storage
            if (this.impactClickId) {
                segmentTraits.impactClickId = this.impactClickId
            }
            segmentIdentify(undefined, segmentTraits)
        } else {
            Sentry.addBreadcrumb({
                category: 'analytics',
                message: 'Segment blocked for setUserAttributes',
                level: 'warning',
                data: {
                    provider: 'segment',
                    method: 'setUserAttributes',
                    analytics: 'segment',
                },
            })
        }
        mixpanel.updateAttributes(attributes)
    }

    /**
     * Set attribution data to be included in all future events
     *
     * This method allows ClickTracker or other components to provide attribution
     * context that will be automatically included in all tracking events.
     *
     * Uses a hybrid attribution model:
     * - Current attribution (last-touch): Stored in sessionStorage, updated each session
     * - Original attribution (first-touch): Stored in localStorage with 30-day TTL, set once
     *
     * @param attribution Attribution data from marketing campaigns, referrals, etc.
     */
    setAttributionData(attribution: Record<string, any>) {
        // MERGE with existing attribution, preserving first-touch UTM params.
        // When users navigate from landing page (with UTM params) to other pages
        // (without UTM params), ClickTracker captures default values like
        // utm_source='direct'. We don't want these defaults to overwrite the
        // actual UTM params captured on the landing page.
        //
        // Strategy: Only overwrite existing values if the new value is "real"
        // (not a default). Otherwise, keep the existing value.
        const merged: Record<string, any> = { ...this.attributionData }

        for (const [key, value] of Object.entries(attribution)) {
            const existingValue = merged[key]
            const hasExistingValue =
                existingValue !== undefined && existingValue !== null

            // Only skip if: new value is a default AND we have an existing real value
            if (isDefaultUtmValue(value) && hasExistingValue) {
                continue // Keep existing value, don't overwrite with default
            }
            merged[key] = value
        }

        // Memory leak fix: Limit attribution data keys
        const keys = Object.keys(merged)
        if (keys.length > this.MAX_ATTRIBUTION_KEYS) {
            if (!isPrd()) {
                console.warn(
                    `Attribution data has ${keys.length} keys, limiting to ${this.MAX_ATTRIBUTION_KEYS}`,
                )
            }
            // Keep only the first MAX_ATTRIBUTION_KEYS keys
            const limitedAttribution: Record<string, any> = {}
            keys.slice(0, this.MAX_ATTRIBUTION_KEYS).forEach((key) => {
                limitedAttribution[key] = merged[key]
            })
            this.attributionData = limitedAttribution
        } else {
            this.attributionData = merged
        }
        this.saveAttributionDataToStorage()

        // First-touch attribution: Save original data if not already set (persists 30 days)
        if (Object.keys(this.originalAttributionData).length === 0) {
            this.originalAttributionData = { ...this.attributionData }
            this.saveOriginalAttributionData()
            debugLog(
                'First-touch attribution saved:',
                this.originalAttributionData,
            )
        }
    }

    /**
     * Get current attribution data
     */
    getAttributionData(): Record<string, any> {
        return { ...this.attributionData }
    }

    /**
     * Get original attribution data (first-touch) with 'original_' prefix.
     * Returns an empty object if no original attribution data is set.
     */
    getOriginalAttributionData(): Record<string, any> {
        const prefixed: Record<string, any> = {}
        for (const [key, value] of Object.entries(
            this.originalAttributionData,
        )) {
            prefixed[`original_${key}`] = value
        }
        return prefixed
    }

    /**
     * Set Impact Radius click ID for Segment referrer context.
     * This is used for Impact.com attribution when tracking events to Segment.
     *
     * @param clickId The Impact Radius click ID from URL (clickid or im_ref param)
     */
    setImpactClickId(clickId: string): void {
        if (!clickId) return

        this.impactClickId = clickId
        this.saveImpactClickIdToStorage()
        debugLog('Impact Click ID set:', clickId)
    }

    /**
     * Get the current Impact Radius click ID
     */
    getImpactClickId(): string | null {
        return this.impactClickId
    }

    /**
     * Process pending events when consent is granted
     * This should be called when the user accepts cookie consent
     */
    async processPendingEvents() {
        if (!hasCookieConsent()) {
            return
        }

        // Now that consent is granted, persist any in-memory data to storage
        // This handles data captured before consent was granted
        this.saveAttributionDataToStorage()
        this.saveOriginalAttributionData()

        // Process pending identity first
        if (this.pendingIdentity) {
            const { userId, attributes } = this.pendingIdentity
            this.pendingIdentity = undefined

            // Store previous anonymous ID for reference if needed
            const previousAnonymousId =
                sessionStorage.getItem('step_session_id')
            if (previousAnonymousId) {
                this.attributionData.previous_anonymous_id = `anonymous_${previousAnonymousId}`
            }

            if (this.braze) {
                this.braze.changeUser(userId)
            } else {
                Sentry.addBreadcrumb({
                    category: 'analytics',
                    message:
                        'Braze blocked for changeUser in processPendingEvents',
                    level: 'warning',
                    data: {
                        provider: 'braze',
                        method: 'processPendingEvents',
                        analytics: 'braze',
                    },
                })
            }
            mixpanel.identifyUser(userId)
            if (window.AF) {
                window.AF('pba', 'setCustomerUserId', userId)
            } else {
                Sentry.addBreadcrumb({
                    category: 'analytics',
                    message:
                        'AppsFlyer blocked for setCustomerUserId in processPendingEvents',
                    level: 'warning',
                    data: {
                        provider: 'appsflyer',
                        method: 'processPendingEvents',
                        analytics: 'appsflyer',
                    },
                })
            }
            if (Object.keys(attributes).length > 0) {
                await this.setUserAttributes(attributes)
            }
        }

        // Process pending events sequentially
        // Re-enrich with current attribution data to fix gaps where events were
        // queued before ClickTracker captured attribution from URL parameters
        const eventsToProcess = [...this.pendingEvents]
        this.pendingEvents = []

        for (const event of eventsToProcess) {
            // Re-enrich properties with current attribution data
            // This ensures events queued before attribution was captured
            // still get UTM params when sent to Segment/Looker
            const reEnrichedProperties = {
                ...this.attributionData,
                ...this.getOriginalAttributionData(),
                ...event.properties, // Original properties take precedence
            }
            await this.sendEvent(event.name, reEnrichedProperties)
            debugLog('Tracked pending event:', event.name, reEnrichedProperties)
        }

        if (eventsToProcess.length > 0) {
            debugLog(`Processed ${eventsToProcess.length} pending events`)
        }

        // Clear pending data from sessionStorage after processing
        this.clearPendingDataFromStorage()
    }

    /**
     * Manually start session recording
     */
    async startSessionRecording() {
        if (!this.initialized) {
            await this.initialize()
        }

        // Check cookie consent before starting session recording
        if (!hasCookieConsent()) {
            return
        }

        mixpanel.startSessionRecording()
    }

    /**
     * Stop session recording
     */
    async stopSessionRecording() {
        if (!this.initialized) {
            await this.initialize()
        }
        mixpanel.stopSessionRecording()
    }

    /**
     * Load original attribution data from localStorage.
     * This data persists across sessions with a 30-day TTL.
     */
    private loadOriginalAttributionData(): void {
        if (typeof window === 'undefined') return

        try {
            const stored = localStorageWithTTL.get<Record<string, any>>(
                this.ORIGINAL_ATTRIBUTION_DATA_KEY,
            )
            if (stored) {
                this.originalAttributionData = stored
            }
        } catch (error) {
            if (!isPrd()) {
                console.warn(
                    'Failed to load original attribution data from localStorage:',
                    error,
                )
            }
            Sentry.captureException(error, {
                tags: {
                    errorType: 'storage_load_failed',
                    analytics: 'analytics_service',
                },
                extra: {
                    storageKey: this.ORIGINAL_ATTRIBUTION_DATA_KEY,
                },
            })
        }
    }

    /**
     * Save original attribution data to localStorage with TTL.
     * Only saves if there's data to save and consent is granted.
     * For GDPR compliance, storage writes are deferred until consent.
     */
    private saveOriginalAttributionData(): void {
        if (typeof window === 'undefined') return
        // Only persist to localStorage if consent granted (GDPR compliance)
        if (!hasCookieConsent()) return

        try {
            if (Object.keys(this.originalAttributionData).length > 0) {
                localStorageWithTTL.set(
                    this.ORIGINAL_ATTRIBUTION_DATA_KEY,
                    this.originalAttributionData,
                    this.ORIGINAL_ATTRIBUTION_TTL_MINUTES,
                )
            }
        } catch (error) {
            if (!isPrd()) {
                console.warn(
                    'Failed to save original attribution data to localStorage:',
                    error,
                )
            }
            Sentry.captureException(error, {
                tags: {
                    errorType: 'storage_save_failed',
                    analytics: 'analytics_service',
                },
                extra: {
                    storageKey: this.ORIGINAL_ATTRIBUTION_DATA_KEY,
                    attributionKeysCount: Object.keys(
                        this.originalAttributionData,
                    ).length,
                },
            })
        }
    }

    /**
     * Start polling to detect blocked analytics providers.
     * Uses progressive polling instead of fixed timeout to:
     * - Exit early when all SDKs load (no unnecessary waiting)
     * - Handle slow connections (up to 10s max)
     * - Avoid false positives from proxy-routed SDK delays
     *
     * Detection methods:
     * - Segment: Cookie presence (ajs_*) - NPM bundle doesn't expose window.analytics
     * - Mixpanel: Cookie presence (mp_*) or session recorder - NPM bundle doesn't expose window.mixpanel
     * - AppsFlyer: window.AF global
     * - gtag: window.dataLayer global
     * - Braze: this.braze instance
     */
    private startBlockedProviderDetection(): void {
        if (typeof window === 'undefined' || this.hasLoggedBlockedProviders) {
            return
        }

        const MAX_WAIT_MS = 10000 // 10s max before giving up
        const POLL_INTERVAL_MS = 1000 // Check every 1s
        const INITIAL_DELAY_MS = 2000 // Wait 2s before first check
        let elapsedMs = 0

        const checkProviders = () => {
            // Check each provider's initialization state
            // Segment NPM bundle doesn't expose window.analytics - check for ajs_ cookie
            const segmentLoaded = document.cookie.includes('ajs_')
            const appsflyerLoaded = !!window.AF
            const gtagLoaded = !!window.dataLayer
            // Mixpanel NPM bundle doesn't expose window.mixpanel - check for cookie or session recorder
            const mixpanelLoaded =
                document.cookie.includes('mp_') || !!window.__mp_recorder
            const brazeLoaded = !!this.braze

            const allLoaded =
                segmentLoaded &&
                appsflyerLoaded &&
                gtagLoaded &&
                mixpanelLoaded &&
                brazeLoaded

            if (allLoaded) {
                // All providers loaded successfully - nothing to report
                return
            }

            elapsedMs += POLL_INTERVAL_MS

            if (elapsedMs >= MAX_WAIT_MS) {
                // Time's up - log whatever is still not loaded as blocked
                this.logBlockedProviders({
                    segmentBlocked: !segmentLoaded,
                    appsflyerBlocked: !appsflyerLoaded,
                    gtagBlocked: !gtagLoaded,
                    mixpanelBlocked: !mixpanelLoaded,
                    brazeBlocked: !brazeLoaded,
                })
            } else {
                // Keep checking
                setTimeout(checkProviders, POLL_INTERVAL_MS)
            }
        }

        // Start checking after initial delay
        setTimeout(checkProviders, INITIAL_DELAY_MS)
    }

    /**
     * Log blocked providers to Sentry (called once per session).
     * Logs both:
     * 1. A combined summary message with boolean tags (backward compatibility)
     * 2. Individual messages per blocked provider for granular dashboard filtering
     */
    private logBlockedProviders(blocked: {
        segmentBlocked: boolean
        appsflyerBlocked: boolean
        gtagBlocked: boolean
        mixpanelBlocked: boolean
        brazeBlocked: boolean
    }): void {
        if (this.hasLoggedBlockedProviders) {
            return
        }

        this.hasLoggedBlockedProviders = true

        const {
            segmentBlocked,
            appsflyerBlocked,
            gtagBlocked,
            mixpanelBlocked,
            brazeBlocked,
        } = blocked

        // Combined summary message for backward compatibility with existing dashboards
        Sentry.captureMessage('Provider Blockage Detected', {
            level: 'info',
            tags: {
                errorType: 'providers_blocked',
                analytics: 'analytics_service',
                'blocked.segment': segmentBlocked,
                'blocked.appsflyer': appsflyerBlocked,
                'blocked.gtag': gtagBlocked,
                'blocked.mixpanel': mixpanelBlocked,
                'blocked.braze': brazeBlocked,
                has_any_block: 'true',
            },
        })

        // Individual messages per blocked provider for granular Sentry filtering
        if (segmentBlocked) {
            Sentry.captureMessage('Segment blocked', {
                level: 'info',
                tags: {
                    errorType: 'provider_blocked',
                    analytics: 'segment',
                },
            })
        }

        if (appsflyerBlocked) {
            Sentry.captureMessage('AppsFlyer blocked', {
                level: 'info',
                tags: {
                    errorType: 'provider_blocked',
                    analytics: 'appsflyer',
                },
            })
        }

        if (gtagBlocked) {
            Sentry.captureMessage('Google Tag Manager blocked', {
                level: 'info',
                tags: {
                    errorType: 'provider_blocked',
                    analytics: 'gtag',
                },
            })
        }

        if (mixpanelBlocked) {
            Sentry.captureMessage('Mixpanel blocked', {
                level: 'info',
                tags: {
                    errorType: 'provider_blocked',
                    analytics: 'mixpanel',
                },
            })
        }

        if (brazeBlocked) {
            Sentry.captureMessage('Braze blocked', {
                level: 'info',
                tags: {
                    errorType: 'provider_blocked',
                    analytics: 'braze',
                },
            })
        }
    }

    /**
     * Build Segment context with IP, user agent, page URL, and email/phone traits
     * for Facebook CAPI matching and Impact Radius referrer for attribution
     */
    private getSegmentContext(): { context: SegmentTrackContext } | undefined {
        const hasIp = !!this.browserContext.ip
        const hasUserAgent = !!this.browserContext.userAgent
        const hasTraits =
            this.currentUserTraits.email || this.currentUserTraits.phone
        const hasImpactClickId = !!this.impactClickId
        const hasPageUrl =
            typeof window !== 'undefined' && !!window.location?.href

        // Return undefined only if we have nothing to add
        if (
            !hasIp &&
            !hasUserAgent &&
            !hasTraits &&
            !hasImpactClickId &&
            !hasPageUrl
        ) {
            return undefined
        }

        const context: SegmentTrackContext = {}

        // Include IP for Meta CAPI attribution
        if (hasIp) {
            context.ip = this.browserContext.ip
        }

        // Include user agent for better device matching
        if (hasUserAgent) {
            context.userAgent = this.browserContext.userAgent
        }

        // Add page URL for Facebook CAPI event_source_url
        if (hasPageUrl) {
            context.page = { url: window.location.href }
        }

        // Add traits for Facebook CAPI matching
        if (hasTraits) {
            const traits: { email?: string; phone?: string } = {}
            if (this.currentUserTraits.email) {
                traits.email = this.currentUserTraits.email
            }
            if (this.currentUserTraits.phone) {
                traits.phone = this.currentUserTraits.phone
            }
            context.traits = traits
        }

        // Add Impact Radius referrer for attribution
        // Impact.com only maps click ID when referrer.type is 'impactRadius'
        if (hasImpactClickId) {
            context.referrer = {
                id: this.impactClickId!,
                type: 'impactRadius',
            }
        }

        return { context }
    }

    /**
     * Build Segment properties with conversion parameters for ad platforms
     * (Google Ads Smart Bidding and Facebook CAPI)
     *
     * Parameters added:
     * - transaction_id: For deduplication (= event_id)
     * - new_customer: For customer acquisition optimization (always true for EarlyPay)
     * - value/currency: For value-based bidding (Underwriting Approved only)
     */
    private buildSegmentConversionProperties(
        baseProperties: Record<string, unknown>,
        segmentEventName: string,
        newCustomer: boolean,
    ): Record<string, unknown> {
        const segmentProperties: Record<string, unknown> = {
            ...baseProperties,
            transaction_id: baseProperties.event_id,
            new_customer: newCustomer,
            // Facebook CAPI required fields (explicit for clarity and debugging)
            action_source: 'website',
            event_source_url:
                typeof window !== 'undefined'
                    ? window.location.href
                    : undefined,
            // Google Ads Enhanced Conversions - include email/phone in properties
            // These are set by identifyUser() via setUserAttributes() earlier in the flow
            // The Segment Google Ads destination maps: coalesce(properties.email, context.traits.email)
            ...(this.currentUserTraits.email && {
                email: this.currentUserTraits.email,
            }),
            ...(this.currentUserTraits.phone && {
                phone: this.currentUserTraits.phone,
            }),
        }

        // For UNDERWRITING_APPROVED, add value from borrowingLimit
        // This enables value-based bidding in Google Ads (Target ROAS)
        // and value optimization in Facebook Ads
        if (
            segmentEventName === AnalyticsEvent.EARLYPAY_UNDERWRITING_APPROVED
        ) {
            const borrowingLimit = baseProperties.borrowingLimit
            if (typeof borrowingLimit === 'string') {
                // Parse "$500" -> 500
                const numericValue = parseFloat(
                    borrowingLimit.replace(/[^0-9.]/g, ''),
                )
                if (!isNaN(numericValue)) {
                    segmentProperties.value = numericValue
                    segmentProperties.currency = 'USD'
                }
            }
        }

        return segmentProperties
    }

    /**
     * Filter PII from attributes before sending to Segment
     * Keeps email/phone for Facebook CAPI matching, and birthday (Segment reserved trait)
     */
    private filterPiiForSegment(
        attributes: UserAttributes,
    ): Partial<UserAttributes> {
        // PII to filter from Segment (keep email/phone for Facebook CAPI, birthday for Segment)
        const piiToFilter = PII_KEYS.filter(
            (key) => key !== 'email' && key !== 'phone' && key !== 'birthday',
        )
        const filtered: Partial<UserAttributes> = {}
        Object.entries(attributes).forEach(([key, value]) => {
            if (!piiToFilter.includes(key)) {
                const f = filtered as Record<string, unknown>
                f[key] = value
            }
        })
        return filtered
    }

    /**
     * Send a single event to all analytics providers
     */
    private async sendEvent(
        eventName: string,
        properties: Record<string, any>,
    ): Promise<void> {
        return new Promise((resolve) => {
            try {
                // Map event name to Segment-specific name if needed
                const segmentEventName =
                    SEGMENT_EVENT_MAP[eventName] ?? eventName

                // Track which providers received the event vs blocked
                const sentTo: string[] = []
                const blockedProviders: string[] = []

                // Mixpanel - always attempt
                mixpanel.track(eventName, {
                    ...properties,
                    hasCookieConsent: hasCookieConsent(),
                })
                sentTo.push('mixpanel')

                // Braze - check if available
                if (this.braze) {
                    this.braze.logCustomEvent(eventName, {
                        ...properties,
                        hasCookieConsent: hasCookieConsent(),
                    })
                    sentTo.push('braze')
                } else {
                    blockedProviders.push('braze')
                }

                // Segment - only send whitelisted events
                if (SEGMENT_ALLOWED_EVENTS.has(segmentEventName)) {
                    if (getSegmentAnalytics()) {
                        // Add conversion parameters for ad platforms (Google Ads, Facebook)
                        const segmentProperties =
                            this.buildSegmentConversionProperties(
                                {
                                    ...properties,
                                    hasCookieConsent: hasCookieConsent(),
                                },
                                segmentEventName,
                                true,
                            )

                        debugLog(
                            'Segment event (from queue):',
                            segmentEventName,
                            segmentProperties,
                        )
                        segmentTrack(
                            segmentEventName,
                            segmentProperties,
                            this.getSegmentContext(),
                        )
                        sentTo.push('segment')
                    } else {
                        blockedProviders.push('segment')
                    }
                }

                // AppsFlyer - check if available
                if (window.AF) {
                    window.AF('pba', 'event', {
                        ...properties,
                        hasCookieConsent: hasCookieConsent(),
                        eventType: 'EVENT',
                        eventName,
                    })
                    sentTo.push('appsflyer')
                } else {
                    blockedProviders.push('appsflyer')
                }

                // Log individual breadcrumbs per blocked provider for granular filtering
                for (const provider of blockedProviders) {
                    Sentry.addBreadcrumb({
                        category: 'analytics',
                        message: `Event blocked: ${eventName}`,
                        level: 'warning',
                        data: {
                            provider,
                            eventName,
                            analytics: provider,
                        },
                    })
                }

                // Add breadcrumb for successful event send
                Sentry.addBreadcrumb({
                    category: 'analytics',
                    message: `Event sent: ${eventName}`,
                    level: 'info',
                    data: {
                        providers: sentTo,
                        hasConsent: hasCookieConsent(),
                    },
                })
            } catch (error) {
                if (!isPrd()) {
                    console.warn('Failed to send event:', eventName, error)
                }
                Sentry.captureException(error, {
                    tags: {
                        errorType: 'event_send_failed',
                        analytics: 'analytics_service',
                    },
                    extra: {
                        eventName,
                        hasConsent: hasCookieConsent(),
                    },
                })
            }
            // Small delay to prevent overwhelming analytics services
            setTimeout(resolve, 50)
        })
    }

    /**
     * Save pending events to sessionStorage.
     * Only saves if consent is granted (GDPR compliance).
     * Events are kept in memory regardless and processed when consent granted.
     */
    private savePendingEventsToStorage() {
        if (typeof window === 'undefined') return
        // Only persist to sessionStorage if consent granted (GDPR compliance)
        if (!hasCookieConsent()) return

        try {
            sessionStorage.setItem(
                this.PENDING_EVENTS_KEY,
                JSON.stringify(this.pendingEvents),
            )
        } catch (error) {
            if (!isPrd()) {
                console.warn(
                    'Failed to save pending events to sessionStorage:',
                    error,
                )
            }
            Sentry.captureException(error, {
                tags: {
                    errorType: 'storage_save_failed',
                    analytics: 'analytics_service',
                },
                extra: {
                    storageKey: this.PENDING_EVENTS_KEY,
                    pendingEventsCount: this.pendingEvents.length,
                },
            })
        }
    }

    /**
     * Save pending identity to sessionStorage.
     * Only saves if consent is granted (GDPR compliance).
     * Identity is kept in memory regardless and processed when consent granted.
     */
    private savePendingIdentityToStorage() {
        if (typeof window === 'undefined') return
        // Only persist to sessionStorage if consent granted (GDPR compliance)
        if (!hasCookieConsent()) return

        try {
            if (this.pendingIdentity) {
                sessionStorage.setItem(
                    this.PENDING_IDENTITY_KEY,
                    JSON.stringify(this.pendingIdentity),
                )
            } else {
                sessionStorage.removeItem(this.PENDING_IDENTITY_KEY)
            }
        } catch (error) {
            if (!isPrd()) {
                console.warn(
                    'Failed to save pending identity to sessionStorage:',
                    error,
                )
            }
            Sentry.captureException(error, {
                tags: {
                    errorType: 'storage_save_failed',
                    analytics: 'analytics_service',
                },
                extra: {
                    storageKey: this.PENDING_IDENTITY_KEY,
                    hasPendingIdentity: !!this.pendingIdentity,
                },
            })
        }
    }

    /**
     * Save attribution data to sessionStorage.
     * Always persists to storage for session recovery - consent check is at send time.
     * This ensures attribution isn't lost if user navigates before accepting cookies.
     */
    private saveAttributionDataToStorage() {
        if (typeof window === 'undefined') return
        // Always persist attribution to sessionStorage for session recovery.
        // Consent is checked at send time, not storage time.
        // This prevents attribution loss when users navigate before accepting cookies.

        try {
            if (Object.keys(this.attributionData).length > 0) {
                sessionStorage.setItem(
                    this.ATTRIBUTION_DATA_KEY,
                    JSON.stringify(this.attributionData),
                )
            } else {
                sessionStorage.removeItem(this.ATTRIBUTION_DATA_KEY)
            }
        } catch (error) {
            if (!isPrd()) {
                console.warn(
                    'Failed to save attribution data to sessionStorage:',
                    error,
                )
            }
            Sentry.captureException(error, {
                tags: {
                    errorType: 'storage_save_failed',
                    analytics: 'analytics_service',
                },
                extra: {
                    storageKey: this.ATTRIBUTION_DATA_KEY,
                    attributionKeysCount: Object.keys(this.attributionData)
                        .length,
                },
            })
        }
    }

    private loadPendingDataFromStorage() {
        if (typeof window === 'undefined') return

        try {
            // Load pending events
            const eventsData = sessionStorage.getItem(this.PENDING_EVENTS_KEY)
            if (eventsData) {
                this.pendingEvents = JSON.parse(eventsData)
            }

            // Load pending identity
            const identityData = sessionStorage.getItem(
                this.PENDING_IDENTITY_KEY,
            )
            if (identityData) {
                this.pendingIdentity = JSON.parse(identityData)
            }

            // Load attribution data
            const attributionData = sessionStorage.getItem(
                this.ATTRIBUTION_DATA_KEY,
            )
            if (attributionData) {
                this.attributionData = JSON.parse(attributionData)
            }

            // Load original attribution data from localStorage (first-touch, persists across sessions)
            this.loadOriginalAttributionData()

            // Load Impact Radius click ID from sessionStorage
            this.loadImpactClickIdFromStorage()
        } catch (error) {
            if (!isPrd()) {
                console.warn(
                    'Failed to load pending data from sessionStorage:',
                    error,
                )
            }
            Sentry.captureException(error, {
                tags: {
                    errorType: 'storage_load_failed',
                    analytics: 'analytics_service',
                },
            })
        }
    }

    private clearPendingDataFromStorage() {
        if (typeof window === 'undefined') return

        try {
            sessionStorage.removeItem(this.PENDING_EVENTS_KEY)
            sessionStorage.removeItem(this.PENDING_IDENTITY_KEY)
        } catch (error) {
            if (!isPrd()) {
                console.warn(
                    'Failed to clear pending data from sessionStorage:',
                    error,
                )
            }
            Sentry.captureException(error, {
                tags: {
                    errorType: 'storage_clear_failed',
                    analytics: 'analytics_service',
                },
            })
        }
    }

    private saveImpactClickIdToStorage(): void {
        if (typeof window === 'undefined' || !this.impactClickId) return

        try {
            sessionStorage.setItem(this.IMPACT_CLICK_ID_KEY, this.impactClickId)
        } catch (error) {
            if (!isPrd()) {
                console.warn(
                    'Failed to save Impact click ID to sessionStorage:',
                    error,
                )
            }
        }
    }

    private loadImpactClickIdFromStorage(): void {
        if (typeof window === 'undefined') return

        try {
            const stored = sessionStorage.getItem(this.IMPACT_CLICK_ID_KEY)
            if (stored) {
                this.impactClickId = stored
            }
        } catch (error) {
            if (!isPrd()) {
                console.warn(
                    'Failed to load Impact click ID from sessionStorage:',
                    error,
                )
            }
        }
    }

    /**
     * Get browser context including user agent and IP address.
     * Returns cached IP (or empty string if not yet fetched) to avoid blocking event handlers.
     * IP fetch is started eagerly in initialize() so it's usually available.
     */
    private getBrowserContext(): { userAgent: string; ip: string } {
        return {
            userAgent: this.getUserAgent(),
            ip: this.browserContext.ip ?? '',
        }
    }

    /**
     * Get the current user agent
     */
    private getUserAgent(): string {
        if (!this.browserContext.userAgent) {
            this.browserContext.userAgent =
                typeof window !== 'undefined' ? navigator.userAgent : ''
        }
        return this.browserContext.userAgent
    }

    /**
     * Fetch client IP address from API route (cached after first fetch)
     */
    private async getClientIp(): Promise<string> {
        // Return cached IP if available
        if (this.browserContext.ip) {
            return this.browserContext.ip
        }

        // If IP fetch previously failed, return empty string immediately
        if (this.ipFetchFailed) {
            return ''
        }

        // If already fetching, wait for that promise
        if (this.ipFetchPromise) {
            return this.ipFetchPromise
        }

        // Start new fetch
        this.ipFetchPromise = (async () => {
            // Memory leak fix: Add timeout to prevent hanging fetch requests
            const controller = new AbortController()
            const timeout = setTimeout(() => controller.abort(), 10000) // 10s

            try {
                const url = `${window.location.origin}/api/get-client-ip`
                const response = await fetch(url, { signal: controller.signal })

                if (!response.ok) {
                    // Only log detailed errors in non-production
                    if (!isPrd()) {
                        const errorDetails = {
                            status: response.status,
                            statusText: response.statusText,
                            url: response.url,
                        }
                        console.error(
                            'Failed to fetch client IP - Response not OK:',
                            errorDetails,
                        )
                    }

                    this.ipFetchFailed = true
                    this.browserContext.ip = ''
                    return ''
                }

                const data = await response.json()
                this.browserContext.ip = data.ip
                return data.ip
            } catch (error) {
                // Only log in non-production to avoid console noise in production
                if (!isPrd()) {
                    console.error('Failed to fetch client IP:', {
                        error,
                        errorMessage:
                            error instanceof Error
                                ? error.message
                                : String(error),
                        origin: window.location.origin,
                    })
                }

                // Mark as failed and cache empty string to avoid repeated failed requests
                this.ipFetchFailed = true
                this.browserContext.ip = ''
                return ''
            } finally {
                clearTimeout(timeout)
                // Clear the promise so subsequent calls can use cached value
                this.ipFetchPromise = undefined
            }
        })()

        return this.ipFetchPromise
    }
}

// Create singleton instance
const analyticsService = new AnalyticsService()

export default analyticsService
